import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MENU } from "@/lib/data";
import Reveal from "@/components/site/Reveal";

export default function Menu() {
  const [hovered, setHovered] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const areaRef = useRef(null);
  const navigate = useNavigate();

  const onMove = (e) => {
    const rect = areaRef.current?.getBoundingClientRect();
    if (rect) setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <section id="menu" className="relative py-24 md:py-40 bg-[#140A0D] border-y border-[#D4AF37]/10">
      <div ref={areaRef} onMouseMove={onMove} className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-4">The Table</p>
              <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-[#FDFBF7] leading-[0.95]">
                Our <span className="gold-gradient-text italic">Menu</span>
              </h2>
            </div>
            <p className="font-body text-[#A89F91] max-w-sm md:text-right">
              A curated selection of our most-loved dishes. View our full catering menu with half & full tray pricing.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <button
            data-testid="view-full-menu-btn"
            onClick={() => navigate("/menu")}
            className="group mb-16 inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase"
          >
            View Full Catering Menu
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </Reveal>

        <div className="space-y-16">
          {MENU.map((group, gi) => (
            <div key={group.category}>
              <Reveal delay={gi * 0.05}>
                <h3 className="font-display italic text-2xl md:text-3xl text-[#7A1F3D] mb-2 flex items-center gap-4">
                  <span className="text-[#D4AF37]">◆</span> {group.category}
                </h3>
              </Reveal>
              <div className="divide-y divide-[#D4AF37]/10 border-t border-[#D4AF37]/10">
                {group.items.map((item, i) => (
                  <Reveal key={item.name} delay={i * 0.04} y={20}>
                    <div
                      data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onMouseEnter={() => setHovered(item)}
                      onMouseLeave={() => setHovered(null)}
                      className="group flex items-baseline gap-4 py-6 md:py-7 cursor-default"
                    >
                      <div className="flex-1">
                        <h4 className="font-display text-2xl md:text-4xl text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors duration-300 group-hover:translate-x-2 transform-gpu will-change-transform">
                          {item.name}
                        </h4>
                        <p className="font-body text-sm md:text-base text-[#A89F91] mt-1 max-w-lg">{item.desc}</p>
                      </div>
                      <span className="font-display text-2xl md:text-3xl text-[#D4AF37] shrink-0">{item.price}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>

        <AnimatePresence>
          {hovered && (
            <motion.div
              key={hovered.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              style={{ left: pos.x, top: pos.y }}
              className="pointer-events-none absolute z-20 hidden lg:block -translate-x-1/2 -translate-y-1/2"
            >
              <div className="w-72 h-80 overflow-hidden rounded-tl-[2.5rem] rounded-br-[2.5rem] border border-[#D4AF37]/40 shadow-2xl">
                <img src={hovered.img} alt={hovered.name} className="w-full h-full object-cover" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
